/**
 * AGROMX Frontend - Tracking Detalle View
 * Vista de detalle de un vehículo con sensores completos y mapa
 */

import trackingService from '../services/tracking.service.js';
import permisos from '../services/permissions.service.js';
import authService from '../services/auth.service.js';
import router from '../router/router.js';

function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve();
  return new Promise((resolve, reject) => {
    window.initMap = resolve;
    const script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyDXUl5_zF6gnVhKYwrTbMHkwgFgb-5x8pM&libraries=geometry&callback=initMap';
    script.async = true;
    script.onerror = () => reject(new Error('Error cargando Google Maps'));
    document.head.appendChild(script);
  });
}

export default {
  data: {
    vehiculo: null,
    posicion: null,
    sensores: null,
    tiempoDetenido: null,
    map: null,
    marker: null
  },

  async render(params) {
    return `
      <div class="page-header">
        <div style="display: flex; align-items: center; gap: 0.75rem;">
          <a href="/admin/tracking" data-link class="btn btn-secondary btn-sm">&larr; Volver</a>
          <h1 id="detalleTitle">Detalle de Activo</h1>
        </div>
      </div>

      <div class="tracking-detalle-layout">
        <!-- Panel izquierdo: info del activo -->
        <div class="tracking-detalle-info">
          <!-- Info del activo -->
          <div class="card" id="activoInfoCard">
            <div class="card-header">
              <h3 class="card-title">Informaci&oacute;n del activo</h3>
            </div>
            <div class="card-body" id="activoInfoBody">
              <div style="padding: 2rem; text-align: center; color: var(--text-muted);">Cargando...</div>
            </div>
          </div>

          <!-- Sensores -->
          <div class="card" id="sensoresCard">
            <div class="card-header">
              <h3 class="card-title">Informaci&oacute;n sensores</h3>
            </div>
            <div class="card-body" id="sensoresBody">
              <div style="padding: 2rem; text-align: center; color: var(--text-muted);">Cargando...</div>
            </div>
          </div>
        </div>

        <!-- Panel derecho: mapa -->
        <div class="tracking-detalle-map">
          <div id="detalleMap" style="width: 100%; height: 100%; min-height: 450px; border-radius: 8px;"></div>
        </div>
      </div>
    `;
  },

  async afterRender(params) {
    window.trackingDetalleView = this;

    try {
      await Promise.all([
        this.cargarDetalle(params.id),
        this.initMap()
      ]);
    } catch (error) {
      console.error('Error cargando detalle:', error);
      window.toast?.error('Error al cargar el detalle del vehículo');
    }
  },

  async cargarDetalle(id) {
    try {
      const response = await trackingService.getVehiculoDetalle(id);
      const data = response.data;
      this.data.vehiculo = data.vehiculo;
      this.data.posicion = data.posicion;
      this.data.sensores = data.sensores;
      this.data.tiempoDetenido = data.tiempo_detenido;

      // Actualizar título
      const title = document.getElementById('detalleTitle');
      if (title) title.textContent = trackingService.etiquetaVehiculo(data.vehiculo);

      this.renderActivoInfo();
      this.renderSensores();
      this.updateMapMarker();
    } catch (error) {
      console.error('Error cargando detalle:', error);
      const body = document.getElementById('activoInfoBody');
      if (body) body.innerHTML = '<div style="padding: 1rem; color: var(--danger);">Error al cargar datos del veh&iacute;culo</div>';
    }
  },

  renderActivoInfo() {
    const body = document.getElementById('activoInfoBody');
    if (!body || !this.data.vehiculo) return;

    const v = this.data.vehiculo;
    const p = this.data.posicion;
    const s = this.data.sensores;
    const estadoTexto = trackingService.getEstadoTexto(s?.ignicion, s?.velocidad);
    const estadoClass = trackingService.getEstadoBadgeClass(s?.ignicion);

    // Botón "Ver Recepción" (atajo a la última recepción visual del tractor)
    let accionesHtml = '';
    if (permisos.canView('recepciones_visuales')) {
      const tieneRecepcion = !!v.ultima_recepcion_id;
      const btnClass = tieneRecepcion ? 'btn-info' : 'btn-secondary';
      const btnDisabled = tieneRecepcion ? '' : 'disabled';
      const btnTitle = tieneRecepcion
        ? 'Abrir la última recepción visual del tractor'
        : 'Este tractor no tiene recepción visual registrada en el sistema';
      const btnText = tieneRecepcion ? 'Ver Recepci&oacute;n' : 'Sin recepci&oacute;n registrada';
      const btnOnclick = tieneRecepcion
        ? `onclick="window.trackingDetalleView.verRecepcion(${v.ultima_recepcion_id})"`
        : '';
      accionesHtml = `
        <div style="margin-top: 1rem; padding-top: 1rem; border-top: 1px solid var(--gray-200); display: flex; gap: 0.5rem; flex-wrap: wrap;">
          <button class="btn btn-sm ${btnClass}" ${btnDisabled} title="${btnTitle}" ${btnOnclick}>${btnText}</button>
        </div>
      `;
    }

    // ERP F1 · B3 (D10): la ficha de la unidad ligada al GPS. «Abrir unidad» sólo con permiso de ver Unidades.
    const ESTADO_U = { en_almacen: 'En almacén', en_produccion: 'En producción', liberado: 'Liberado', entregado: 'Entregado', en_servicio: 'En servicio',
      por_llegar: 'Por llegar', en_transito: 'En tránsito', en_consigna: 'En consigna', vendido: 'Vendido', recuperado: 'Recuperado', baja: 'Baja' };
    const TIPO_U = { tractor: 'Tractor', implemento: 'Implemento', dron: 'Dron', vehiculo: 'Vehículo', maquinaria_ligera: 'Maquinaria ligera' };
    const unidadHtml = v.unidad_num_serie
      ? `<strong>${v.unidad_num_serie}</strong> · ${TIPO_U[v.unidad_tipo] || v.unidad_tipo || ''} ${v.unidad_modelo || ''}
         <span class="badge badge-info">${ESTADO_U[v.unidad_estado] || v.unidad_estado || ''}</span>
         ${v.unidad_alta_origen === 'gps' ? '<span class="badge badge-warning" title="Nunca pasó por recepción">entró del GPS</span>' : ''}
         ${permisos.canView('tractores') ? `<a href="/unidades?ver=${encodeURIComponent(v.unidad_num_serie)}" data-link class="btn btn-sm btn-info" style="margin-left:.4rem;">Abrir unidad</a>` : ''}`
      : '<span style="color:var(--gray-500);font-style:italic;">Este GPS no está ligado a ninguna unidad</span>';

    body.innerHTML = `
      <table class="tracking-info-table">
        <tr><td class="info-label">Unidad:</td><td class="info-value">${unidadHtml}</td></tr>
        <tr><td class="info-label">Placa:</td><td class="info-value">${v.placa}</td></tr>
        <tr><td class="info-label">Serie:</td><td class="info-value">${trackingService.etiquetaVehiculo(v)}</td></tr>
        ${!authService.hasRole('distribuidor') ? `<tr><td class="info-label">Distribuidor:</td><td class="info-value">${v.distribuidor_nombre || '<span style="color:var(--gray-500);font-style:italic;">Sin asignar</span>'}</td></tr>` : ''}
        <tr><td class="info-label">Nombre GPS:</td><td class="info-value">${v.nombre_activo || 'N/A'}</td></tr>
        <tr><td class="info-label">Dispositivo:</td><td class="info-value">${v.imei || 'N/A'}</td></tr>
        <tr><td class="info-label">Ignici&oacute;n:</td><td class="info-value"><span class="${estadoClass}">${estadoTexto}</span></td></tr>
        <tr><td class="info-label">Posici&oacute;n:</td><td class="info-value">${p?.latitude ? `${p.latitude}, ${p.longitude}` : 'Sin datos'}</td></tr>
        <tr>
          <td class="info-label">Link Google Maps:</td>
          <td class="info-value">
            ${p?.latitude ? `<a href="https://www.google.com/maps?q=${p.latitude},${p.longitude}" target="_blank" style="color: var(--primary);">Ver Mapa</a>` : 'N/A'}
          </td>
        </tr>
        <tr><td class="info-label">&Uacute;ltimo reporte:</td><td class="info-value">${trackingService.formatFechaHora(p?.fecha_hora)}</td></tr>
        <tr><td class="info-label">&Uacute;ltima conexi&oacute;n:</td><td class="info-value">${trackingService.formatFechaHora(p?.recibido_en)}</td></tr>
        <tr><td class="info-label">Tiempo detenido:</td><td class="info-value">${this.data.tiempoDetenido || 'N/A'}</td></tr>
        ${p?.direccion ? `<tr><td class="info-label">Domicilio:</td><td class="info-value">${p.direccion}</td></tr>` : ''}
        <tr><td class="info-label">Hor&oacute;metro:</td><td class="info-value">${s?.horometro ? `<span class="badge badge-warning">${s.horometro} Hrs.</span>` : 'N/A'}</td></tr>
        <tr><td class="info-label">Od&oacute;metro Total:</td><td class="info-value">${s?.odometro ? `<span class="badge badge-info">${(parseFloat(s.odometro) / 1000).toFixed(3)} km</span>` : 'N/A'}</td></tr>
      </table>
      ${accionesHtml}
    `;
  },

  verRecepcion(recepcionId) {
    if (!recepcionId) return;
    router.navigate(`/almacen/recepciones/${recepcionId}`);
  },

  renderSensores() {
    const body = document.getElementById('sensoresBody');
    if (!body || !this.data.sensores) return;

    const s = this.data.sensores;

    body.innerHTML = `
      <table class="tracking-info-table">
        <tr>
          <td class="info-label">Pie en descanso:</td>
          <td class="info-value">${s.pie_en_descanso !== null && s.pie_en_descanso !== undefined ? `${s.pie_en_descanso} s` : 'N/A'}</td>
        </tr>
        <tr>
          <td class="info-label">Uso de clutch:</td>
          <td class="info-value">${s.uso_clutch !== null ? `${s.uso_clutch} h` : 'N/A'}</td>
        </tr>
        <tr>
          <td class="info-label">Motor encendido:</td>
          <td class="info-value">${s.ignicion ? '<span style="color:var(--success);">S&iacute;</span>' : '<span style="color:var(--danger);">No</span>'} ${!s.ignicion && s.velocidad > 0 ? '(switch apagado con movimiento)' : ''}</td>
        </tr>
        <tr>
          <td class="info-label">Temperatura de motor:</td>
          <td class="info-value">${s.temperatura_motor !== null ? `${s.temperatura_motor} &deg;C` : 'N/A'}</td>
        </tr>
        <tr>
          <td class="info-label">Presi&oacute;n de aceite:</td>
          <td class="info-value">${s.presion_aceite || 'N/A'}</td>
        </tr>
        <tr>
          <td class="info-label">Velocidad:</td>
          <td class="info-value">${s.velocidad} km/h</td>
        </tr>
        <tr>
          <td class="info-label">Direcci&oacute;n:</td>
          <td class="info-value">${s.direccion} ${s.direccion_texto || ''}</td>
        </tr>
        <tr>
          <td class="info-label">Se&ntilde;al:</td>
          <td class="info-value">${s.senal !== null ? s.senal : 'N/A'}</td>
        </tr>
        <tr>
          <td class="info-label">Bater&iacute;a dispositivo:</td>
          <td class="info-value">${s.bateria_gps !== null ? `${s.bateria_gps} %` : 'N/A'}</td>
        </tr>
        <tr>
          <td class="info-label">Bater&iacute;a activo:</td>
          <td class="info-value">${s.bateria_vehiculo !== null ? `${s.bateria_vehiculo} V` : 'N/A'}</td>
        </tr>
      </table>
    `;
  },

  async initMap() {
    try {
      await loadGoogleMaps();

      const mapElement = document.getElementById('detalleMap');
      if (!mapElement) return;

      this.data.map = new google.maps.Map(mapElement, {
        center: { lat: 19.3, lng: -98.2 },
        zoom: 10,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true
      });

      if (this.data.posicion?.latitude) {
        this.updateMapMarker();
      }
    } catch (error) {
      console.error('Error inicializando mapa:', error);
      const mapElement = document.getElementById('detalleMap');
      if (mapElement) {
        mapElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:var(--text-muted);">Error al cargar el mapa</div>';
      }
    }
  },

  updateMapMarker() {
    if (!this.data.map || !this.data.posicion) return;

    const { latitude, longitude } = this.data.posicion;
    const la = parseFloat(String(latitude).replace(',', '.'));
    const lo = parseFloat(String(longitude).replace(',', '.'));
    if (!Number.isFinite(la) || !Number.isFinite(lo) || Math.abs(la) > 90 || Math.abs(lo) > 180) return;

    const position = { lat: la, lng: lo };

    if (this.data.marker) {
      this.data.marker.setPosition(position);
    } else {
      const ignicion = this.data.sensores?.ignicion;
      this.data.marker = new google.maps.Marker({
        position,
        map: this.data.map,
        title: trackingService.etiquetaVehiculo(this.data.vehiculo),
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: ignicion ? '#28a745' : '#dc3545',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
          scale: 10
        }
      });
    }

    this.data.map.setCenter(position);
    this.data.map.setZoom(15);
  }
};
